// Thin fetch wrapper. All API calls go through `apiFetch` so the bearer
// token and error handling live in one place. The token is read from the
// auth store at call time, not closed over, so login/logout takes effect
// without re-creating the API client.

import { useAuthStore, useUiStore } from './stores.js';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

function apiUrl(path) {
  if (!API_BASE || /^https?:\/\//i.test(path)) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function apiFetch(path, { method = 'GET', body, headers = {}, signal } = {}) {
  const token = useAuthStore.getState().token;
  const init = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    signal,
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(apiUrl(path), init);
  if (res.status === 204) return null;

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    if (!res.ok) throw new ApiError(res.statusText, res.status, 'http_error');
  }

  if (!res.ok) {
    const err = payload?.error || {};
    // A revoked / expired session on the server means the token in the
    // store is dead. Wipe it and re-prompt; the user's auth flow will
    // pick a new one and the failing query can be retried.
    if (res.status === 401) {
      const auth = useAuthStore.getState();
      if (auth.token) {
        auth.clear();
        useUiStore.getState().openModal('login');
      }
    }
    throw new ApiError(err.message || res.statusText, res.status, err.code || 'http_error', err.details);
  }
  return payload;
}

export { ApiError };

// --- Auth ----------------------------------------------------------------

export const authApi = {
  login: (email, password, remember_me = true) =>
    apiFetch('/api/auth/login', { method: 'POST', body: { email, password, remember_me } }),

  register: (data) => apiFetch('/api/auth/register', { method: 'POST', body: data }),

  logout: () => apiFetch('/api/auth/logout', { method: 'POST' }),

  me: () => apiFetch('/api/me'),

  passwordResetRequest: (email) =>
    apiFetch('/api/auth/password-reset/request', { method: 'POST', body: { email } }),

  passwordResetConfirm: (data) =>
    apiFetch('/api/auth/password-reset/confirm', { method: 'POST', body: data }),

  // `email` is optional — required for pending users who aren't logged
  // in yet (no bearer token to identify them). Logged-in users can call
  // without arguments and the server picks them off the session.
  requestEmailVerify: (email) =>
    apiFetch('/api/auth/email/verify/request', {
      method: 'POST',
      body: email ? { email } : {},
    }),

  confirmEmailVerify: (token) =>
    apiFetch('/api/auth/email/verify', { method: 'POST', body: { token } }),

  acceptTerms: (data = {}) =>
    apiFetch('/api/me/terms/accept', {
      method: 'POST',
      body: { accepted: true, source: 'terms_page', ...data },
    }),
};

// --- Catalog -------------------------------------------------------------

export const catalogApi = {
  categories: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiFetch(`/api/catalog/categories${q ? `?${q}` : ''}`);
  },

  channels: (params = {}) => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
    ).toString();
    return apiFetch(`/api/catalog/channels${q ? `?${q}` : ''}`);
  },

  // Map of channel_id -> { title, start_at, end_at, description } for every
  // channel currently airing a program. Returned in one query, cached for
  // 60s per-org on the backend.
  channelsNow: () => apiFetch('/api/catalog/channels/now'),

  channel: (slug) => apiFetch(`/api/catalog/channels/${slug}`),

  channelEpg: (slug, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiFetch(`/api/catalog/channels/${slug}/epg${q ? `?${q}` : ''}`);
  },
};

// --- Public plans --------------------------------------------------------

export const plansApi = {
  list: () => apiFetch('/api/plans'),
};

// --- Favorites -----------------------------------------------------------

export const favoritesApi = {
  list: (kind = 'channel') => apiFetch(`/api/favorites?kind=${kind}`),

  add: (kind, item_id) =>
    apiFetch('/api/favorites', { method: 'POST', body: { kind, item_id } }),

  remove: (favorite_id) =>
    apiFetch(`/api/favorites/${favorite_id}`, { method: 'DELETE' }),
};

// --- Premieres -----------------------------------------------------------

export const premieresApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiFetch(`/api/premieres${q ? `?${q}` : ''}`);
  },

  subscriptions: () => apiFetch('/api/premieres/subscriptions'),

  subscribe: (premiere_id, auto_switch = true) =>
    apiFetch(`/api/premieres/${premiere_id}/subscribe`, {
      method: 'POST',
      body: { auto_switch },
    }),

  unsubscribe: (premiere_id) =>
    apiFetch(`/api/premieres/${premiere_id}/subscribe`, { method: 'DELETE' }),

  pendingSwitch: () => apiFetch('/api/premieres/pending-switch'),

  // SSE URL. EventSource can't send headers so the bearer goes in the query.
  eventsUrl: (token) => apiUrl(`/api/premieres/events?token=${encodeURIComponent(token)}`),
};

// --- Streams -------------------------------------------------------------

// --- Watchlist -----------------------------------------------------------
//
// Personal list of EpgProgram rows a user wants to watch. Each row has an
// auto_switch flag that defaults to off — only items with auto_switch on
// trigger an automatic channel change at start time.

export const watchlistApi = {
  list: () => apiFetch('/api/watchlist'),
  add: (epgProgramId, opts = {}) =>
    apiFetch('/api/watchlist', {
      method: 'POST',
      body: { epg_program_id: epgProgramId, ...opts },
    }),
  update: (itemId, patch) =>
    apiFetch(`/api/watchlist/${itemId}`, { method: 'PATCH', body: patch }),
  remove: (itemId) => apiFetch(`/api/watchlist/${itemId}`, { method: 'DELETE' }),
  pendingSwitch: () => apiFetch('/api/watchlist/pending-switch'),
  eventsUrl: (token) => apiUrl(`/api/watchlist/events?token=${encodeURIComponent(token)}`),
  // Confirm the client handled a switch event. Until this is called the
  // item stays pending — closing the tab before acking means the same
  // banner reappears on the next session.
  ack: (itemId) => apiFetch(`/api/watchlist/${itemId}/ack`, { method: 'POST' }),
};

export const streamsApi = {
  active: () => apiFetch('/api/streams/active'),
  heartbeat: (data) => apiFetch('/api/streams/heartbeat', { method: 'POST', body: data }),
  stop: (data) => apiFetch('/api/streams/stop', { method: 'POST', body: data }),
  stopAll: ({ keepCurrent = false } = {}) =>
    apiFetch('/api/streams/stop-all', { method: 'POST', body: { keep_current: keepCurrent } }),
};

// --- Billing -------------------------------------------------------------

export const billingApi = {
  account: () => apiFetch('/api/billing/account'),
  checkout: (data) => apiFetch('/api/billing/checkout', { method: 'POST', body: data }),
};

// Build the same-origin watch URL the React app actually points the player
// at. Same shape Django generates, but relative so it travels through the
// Vite proxy in dev and the reverse proxy in prod.
export function buildWatchUrl(kind, itemId, token) {
  if (!token) return '';
  return apiUrl(`/watch/${kind}/${token}/${itemId}`);
}
